import { Box, Button, CircularProgress, Modal, Typography, Fade, Backdrop, IconButton } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close';
import { isMobile } from 'react-device-detect';
import { styled } from '@mui/material/styles';

// Componentes estilizados para um design moderno
const StyledModal = styled(Modal)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
    },
}));

const ModalContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
    outline: 'none',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
}));

const ModalHeader = styled(Box)(({ theme }) => ({
    padding: '24px 24px 16px 24px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
    position: 'relative',
}));

const ModalContent = styled(Box)(({ theme }) => ({
    padding: '24px',
    flex: 1,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(0, 0, 0, 0.3)',
    },
}));

const ModalFooter = styled(Box)(({ theme }) => ({
    padding: '16px 24px 24px 24px',
    borderTop: '1px solid rgba(0, 0, 0, 0.08)',
    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
}));

const StyledButton = styled(Button)(({ theme, variant }) => ({
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    padding: '10px 24px',
    fontSize: '14px',
    minWidth: '100px',
    ...(variant === 'contained' && {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
        '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
            transform: 'translateY(-1px)',
        },
        '&:disabled': {
            background: 'rgba(0, 0, 0, 0.12)',
            color: 'rgba(0, 0, 0, 0.26)',
            boxShadow: 'none',
        },
    }),
    ...(variant === 'outlined' && {
        border: '2px solid #e2e8f0',
        color: '#64748b',
        '&:hover': {
            border: '2px solid #cbd5e1',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            transform: 'translateY(-1px)',
        },
    }),
    transition: 'all 0.2s ease-in-out',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '16px',
    right: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: '#64748b',
    '&:hover': {
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        color: '#ef4444',
        transform: 'scale(1.1)',
    },
    transition: 'all 0.2s ease-in-out',
}));

const Title = styled(Typography)(({ theme }) => ({
    fontSize: '24px',
    fontWeight: 700,
    color: '#331966',
    letterSpacing: '-0.025em',
    lineHeight: 1.2,
}));

export const PetModal = ({ children, onClose, open, title, size, formId, loading }) => {


    const TYPES = {
        WIDTH: 'width',
        HEIGHT: 'height',
    }

    const resolveSize = (received, type) => {
        if (isMobile) {
            return type === TYPES.WIDTH ? "85vw" : "70vh";
        }

        const sizes = {
            small: { width: "400px", height: "auto", maxHeight: "50vh" },
            middle: { width: "600px", height: "auto", maxHeight: "70vh" },
            'big-big': { width: "800px", height: "auto", maxHeight: "80vh" },
            default: { width: "500px", height: "auto", maxHeight: "60vh" }
        };

        const sizeConfig = sizes[received] || sizes.default;
        return sizeConfig[type === TYPES.WIDTH ? 'width' : 'maxHeight'];
    }

    return (
        <StyledModal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 300,
            }}
        >
            <Fade in={open} timeout={300}>
                <ModalContainer
                    width={resolveSize(size, TYPES.WIDTH)}
                    maxHeight={resolveSize(size, TYPES.HEIGHT)}
                >

                    <ModalHeader>
                        {title && <Title>{title}</Title>}
                        <CloseButton onClick={onClose} size="small">
                            <CloseIcon fontSize="small" />
                        </CloseButton>
                    </ModalHeader>

                    <ModalContent>
                        {children}
                    </ModalContent>

                    <ModalFooter>
                        <StyledButton
                            variant="outlined"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </StyledButton>
                        {formId ? (
                            <StyledButton
                                type="submit"
                                form={formId}
                                variant="contained"
                                disabled={loading}
                            >
                                {loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    'Salvar'
                                )}
                            </StyledButton>
                        ) : (
                            <StyledButton
                                variant="contained"
                                disabled={loading}
                            >
                                {loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    'Salvar'
                                )}
                            </StyledButton>
                        )}
                    </ModalFooter>
                </ModalContainer>
            </Fade>
        </StyledModal>
    )
}

